<template>
  <transition name="cl-backdrop" @after-leave="$emit('close')">
    <div
      v-if="isVisible"
      class="cl-backdrop"
      role="dialog"
      aria-modal="true"
      :aria-label="`Image ${currentMediaIndex + 1} of ${media.length}`"
      @click.self="closeLightbox"
    >
      <div class="cl-vignette" aria-hidden="true"></div>

      <div v-if="media.length > 1" class="cl-counter" aria-hidden="true">
        <span class="cl-counter__current">{{ pad(currentMediaIndex + 1) }}</span>
        <span class="cl-counter__sep">/</span>
        <span class="cl-counter__total">{{ pad(media.length) }}</span>
      </div>

      <button
        type="button"
        class="cl-close"
        aria-label="Close gallery"
        @click="closeLightbox"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div class="cl-stage" @click.self="closeLightbox">
        <button
          v-if="media.length > 1"
          type="button"
          class="cl-nav cl-nav--prev"
          aria-label="Previous image"
          @click="prevMedia"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>

        <transition :name="transitionName" mode="out-in">
          <div :key="currentMediaIndex" class="cl-frame">
            <!-- Playable items come first: a YouTube item is an embedded
                 player, an uploaded clip is a native <video>. Both are keyed
                 by currentMediaIndex on the wrapping .cl-frame, so navigating
                 away tears the element down and playback stops. -->
            <!-- Keep `autoplay` in the allow list: embedOf() sends autoplay=1 and
                 a cross-origin embed is denied autoplay without the delegation,
                 so trimming it here silently stops videos from starting. -->
            <iframe
              v-if="kindOf(currentMedia) === 'youtube'"
              :src="embedOf(currentMedia)"
              class="cl-video"
              :title="altOf(currentMedia) || 'Campaign video'"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
            <video
              v-else-if="kindOf(currentMedia) === 'video'"
              :src="videoOf(currentMedia)"
              class="cl-video"
              controls
              autoplay
              playsinline
              :poster="srcOf(currentMedia) || null"
            ></video>
            <img
              v-else-if="isImage(srcOf(currentMedia))"
              :src="srcOf(currentMedia)"
              :alt="altOf(currentMedia)"
              class="cl-image"
              :class="{ 'cl-image--loaded': loaded }"
              @load="loaded = true"
              @error="loaded = true"
            />
            <div v-else class="cl-placeholder" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                <circle cx="12" cy="13" r="3" />
              </svg>
            </div>
          </div>
        </transition>

        <button
          v-if="media.length > 1"
          type="button"
          class="cl-nav cl-nav--next"
          aria-label="Next image"
          @click="nextMedia"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      <div class="cl-footer">
        <p v-if="altOf(currentMedia)" class="cl-caption">{{ altOf(currentMedia) }}</p>

        <div v-if="media.length > 1" ref="rail" class="cl-rail" aria-label="Gallery thumbnails">
          <button
            v-for="(item, i) in media"
            :key="i"
            ref="thumbs"
            type="button"
            class="cl-thumb"
            :class="{ 'cl-thumb--active': i === currentMediaIndex }"
            :aria-label="`Go to image ${i + 1}`"
            :aria-current="i === currentMediaIndex ? 'true' : null"
            @click="jumpTo(i)"
          >
            <img v-if="srcOf(item)" :src="srcOf(item)" alt="" loading="lazy" />
            <span v-else class="cl-thumb__play" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.2v13.6L19 12z" /></svg>
            </span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  emits: ['close'],
  props: {
    /**
     * Gallery items. Accepts plain URL strings (legacy) OR objects
     * `{ src, alt }`. The object form unlocks captions in the footer.
     */
    media: {
      type: Array,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentMediaIndex: this.index,
      isVisible: this.visible,
      // Direction-aware transition name set right before the index
      // change so prev/next slide the image in the matching direction.
      transitionName: 'cl-fade',
      // Toggled per image so the frame fades in once decoded.
      loaded: false,
      previousFocus: null,
    };
  },
  computed: {
    currentMedia() {
      return this.media[this.currentMediaIndex];
    },
  },
  watch: {
    index(val) {
      this.currentMediaIndex = val;
    },
    visible(val) {
      this.isVisible = val;
    },
    isVisible(val) {
      if (val) {
        this.lockBodyScroll(true);
        this.previousFocus = document.activeElement;
        this.$nextTick(this.centerActiveThumb);
      } else {
        this.lockBodyScroll(false);
        this.previousFocus?.focus?.();
      }
    },
    currentMediaIndex() {
      this.loaded = false;
      this.$nextTick(this.centerActiveThumb);
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown);
    if (this.isVisible) {
      this.lockBodyScroll(true);
      this.previousFocus = document.activeElement;
      this.$nextTick(this.centerActiveThumb);
    }
  },
  unmounted() {
    window.removeEventListener('keydown', this.onKeydown);
    this.lockBodyScroll(false);
  },
  methods: {
    closeLightbox() {
      // Toggle the inner v-if so the leave transition plays; `close`
      // is emitted from the wrapping transition's `@after-leave` so
      // the parent can keep its own v-if guard.
      this.isVisible = false;
    },
    prevMedia() {
      if (this.media.length < 2) return;
      this.transitionName = 'cl-slide-right';
      this.currentMediaIndex = (this.currentMediaIndex - 1 + this.media.length) % this.media.length;
    },
    nextMedia() {
      if (this.media.length < 2) return;
      this.transitionName = 'cl-slide-left';
      this.currentMediaIndex = (this.currentMediaIndex + 1) % this.media.length;
    },
    jumpTo(i) {
      if (i === this.currentMediaIndex) return;
      this.transitionName = i > this.currentMediaIndex ? 'cl-slide-left' : 'cl-slide-right';
      this.currentMediaIndex = i;
    },
    onKeydown(e) {
      if (!this.isVisible) return;
      if (e.key === 'Escape') { this.closeLightbox(); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { this.prevMedia(); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { this.nextMedia(); e.preventDefault(); }
      else if (e.key === 'Home') { this.jumpTo(0); e.preventDefault(); }
      else if (e.key === 'End') { this.jumpTo(this.media.length - 1); e.preventDefault(); }
    },
    /**
     * 'youtube' | 'video' | 'image'. Items arrive either as plain url strings
     * (legacy callers) or as `{ src, alt, kind, videoUrl }` from V2About; only
     * the object form can be playable.
     */
    kindOf(item) {
      if (!item || typeof item === 'string') return 'image';
      if (item.kind === 'youtube' || item.kind === 'video') {
        // A kind is only honoured when there is actually something to play —
        // otherwise the frame would render an empty player.
        return item.videoUrl ? item.kind : 'image';
      }
      return 'image';
    },
    embedOf(item) {
      const url = (item && item.videoUrl) || '';
      // autoplay=1 saves the visitor a second click inside the embed. It only
      // works because the iframe is always built in response to a user gesture
      // (openers and navigation alike) AND carries allow="autoplay", without
      // which a cross-origin embed is denied autoplay outright — necessary,
      // though not sufficient: each browser still applies its own policy on
      // top. A browser that refuses unmuted autoplay (iOS in particular)
      // leaves the embed paused, so the visitor still gets a play button.
      // rel=0 keeps YouTube's end-screen suggestions inside this channel and
      // playsinline stops iOS from taking over the whole screen.
      const params = 'autoplay=1&rel=0&playsinline=1';
      return url ? `${url}${url.includes('?') ? '&' : '?'}${params}` : '';
    },
    videoOf(item) {
      return (item && item.videoUrl) || '';
    },
    isImage(url) {
      // Permissive: matches CDN URLs that may include a query string,
      // and accepts modern formats (webp, svg) the old regex missed.
      return /\.(jpg|jpeg|png|gif|bmp|webp|svg|avif)(\?.*)?$/i.test(url || '');
    },
    srcOf(item) {
      if (!item) return '';
      return typeof item === 'string' ? item : (item.src || '');
    },
    altOf(item) {
      if (!item) return '';
      return typeof item === 'string' ? '' : (item.alt || '');
    },
    pad(n) {
      return String(n).padStart(2, '0');
    },
    centerActiveThumb() {
      const thumbs = this.$refs.thumbs;
      if (!thumbs) return;
      const active = thumbs[this.currentMediaIndex];
      active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    },
    lockBodyScroll(lock) {
      // Direct style toggle (not a global class) because scoped styles
      // can't reliably reach <body> across all build pipelines.
      const body = document.body;
      if (!body) return;
      if (lock) {
        if (body.dataset.clScrollLocked) return;
        body.dataset.clPrevOverflow = body.style.overflow || '';
        body.style.overflow = 'hidden';
        body.dataset.clScrollLocked = '1';
      } else {
        if (!body.dataset.clScrollLocked) return;
        body.style.overflow = body.dataset.clPrevOverflow || '';
        delete body.dataset.clPrevOverflow;
        delete body.dataset.clScrollLocked;
      }
    },
  },
};
</script>

<style scoped>
/* ─── Backdrop ─────────────────────────────────────────── */
.cl-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 24px;
  background:
    linear-gradient(135deg,
      rgba(15, 21, 46, 0.97) 0%,
      rgba(29, 37, 73, 0.94) 50%,
      rgba(15, 21, 46, 0.97) 100%);
  backdrop-filter: blur(16px) saturate(125%);
  -webkit-backdrop-filter: blur(16px) saturate(125%);
  font-family: 'Red Hat Display', sans-serif;
  color: #ffffff;
}
.cl-vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 45%, rgba(218, 24, 132, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

/* ─── Counter (top-left) ───────────────────────────────── */
.cl-counter {
  position: absolute;
  top: 26px;
  left: 32px;
  z-index: 2;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  user-select: none;
}
.cl-counter__current { color: var(--sideout-pink, #da1884); }
.cl-counter__sep     { color: rgba(255, 255, 255, 0.4); padding: 0 2px; }
.cl-counter__total   { color: rgba(255, 255, 255, 0.85); }

/* ─── Close button (top-right) ─────────────────────────── */
.cl-close {
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 2;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.94);
  color: var(--sideout-navy, #1d2549);
  transition: transform 180ms ease, background-color 180ms ease, box-shadow 220ms ease;
  backdrop-filter: blur(8px);
}
.cl-close svg { width: 16px; height: 16px; display: block; }
.cl-close:hover {
  background: #ffffff;
  transform: scale(1.06);
  box-shadow: 0 0 0 6px rgba(218, 24, 132, 0.16);
}
.cl-close:active { transform: scale(0.96); }
.cl-close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--sideout-pink, #da1884);
}

/* ─── Stage (image + nav buttons) ──────────────────────── */
.cl-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  justify-items: center;
  gap: 24px;
  padding: 8px 4px;
}
.cl-frame {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 12px;
  padding-bottom: 14px;
  border-radius: 4px;
  max-width: 90vw;
  max-height: 70vh;
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(255, 255, 255, 0.06),
    inset 0 0 0 1px rgba(15, 21, 46, 0.06);
}
.cl-image {
  display: block;
  max-width: calc(90vw - 24px);
  max-height: calc(70vh - 26px);
  object-fit: contain;
  border-radius: 2px;
  opacity: 0;
  filter: blur(6px);
  transition: opacity 260ms cubic-bezier(0.4, 0, 0.2, 1),
              filter 260ms cubic-bezier(0.4, 0, 0.2, 1);
}
.cl-image--loaded {
  opacity: 1;
  filter: blur(0);
}
.cl-placeholder {
  width: 280px;
  height: 180px;
  color: rgba(15, 21, 46, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.cl-placeholder svg { width: 48px; height: 48px; }

/* Playable media fills the same box the image would, at 16:9 — the aspect
   both YouTube embeds and campaign uploads are authored in. */
.cl-video {
  display: block;
  width: min(calc(90vw - 24px), calc((70vh - 26px) * 16 / 9));
  aspect-ratio: 16 / 9;
  max-height: calc(70vh - 26px);
  border: 0;
  border-radius: 2px;
  background: #000000;
}

/* ─── Nav buttons ──────────────────────────────────────── */
.cl-nav {
  width: 52px;
  height: 52px;
  border: 0;
  border-radius: 9999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.94);
  color: var(--sideout-pink, #da1884);
  transition: transform 200ms ease, background-color 200ms ease, box-shadow 240ms ease;
  backdrop-filter: blur(8px);
  z-index: 1;
}
.cl-nav svg { width: 22px; height: 22px; display: block; }
.cl-nav:hover {
  background: #ffffff;
  transform: scale(1.06);
  box-shadow:
    0 0 0 8px rgba(218, 24, 132, 0.14),
    0 14px 30px -10px rgba(0, 0, 0, 0.4);
}
.cl-nav:active { transform: scale(0.97); }
.cl-nav:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--sideout-pink, #da1884);
}

/* ─── Footer: caption + thumbnail rail ─────────────────── */
.cl-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding-top: 4px;
}
.cl-caption {
  margin: 0;
  max-width: 80vw;
  text-align: center;
  text-wrap: balance;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  letter-spacing: 0.01em;
  line-height: 1.4;
}
.cl-rail {
  display: flex;
  gap: 8px;
  max-width: min(92vw, 720px);
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 8px 4px 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}
.cl-rail::-webkit-scrollbar { height: 6px; }
.cl-rail::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}
.cl-thumb {
  flex: 0 0 auto;
  width: 72px;
  height: 56px;
  border: 0;
  padding: 0;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.06);
  opacity: 0.55;
  transition: opacity 220ms ease, transform 220ms ease, box-shadow 220ms ease;
}
.cl-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cl-thumb__play {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 21, 46, 0.6);
  color: #ffffff;
}
.cl-thumb__play svg { width: 18px; height: 18px; margin-left: 2px; }
.cl-thumb:hover { opacity: 0.9; transform: scale(1.04); }
.cl-thumb--active {
  opacity: 1;
  transform: scale(1.05);
  box-shadow:
    0 0 0 2px var(--sideout-pink, #da1884),
    0 8px 20px -8px rgba(218, 24, 132, 0.4);
}
.cl-thumb:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--sideout-pink, #da1884);
}

/* ─── Transitions ──────────────────────────────────────── */
.cl-backdrop-enter-active,
.cl-backdrop-leave-active {
  transition: opacity 280ms cubic-bezier(0.4, 0, 0.2, 1);
}
.cl-backdrop-enter-from,
.cl-backdrop-leave-to {
  opacity: 0;
}

/* Image slide-fade between indices. Direction is set on `transitionName`
   right before the index change in prev/next/jumpTo, so the image
   moves *with* the navigation direction (next = leaves left, enters
   from right; prev = leaves right, enters from left). */
.cl-fade-enter-active,
.cl-fade-leave-active,
.cl-slide-left-enter-active,
.cl-slide-left-leave-active,
.cl-slide-right-enter-active,
.cl-slide-right-leave-active {
  transition: opacity 220ms cubic-bezier(0.4, 0, 0.2, 1),
              transform 220ms cubic-bezier(0.4, 0, 0.2, 1);
}
.cl-fade-enter-from,
.cl-fade-leave-to { opacity: 0; }

.cl-slide-left-enter-from  { opacity: 0; transform: translateX(24px); }
.cl-slide-left-leave-to    { opacity: 0; transform: translateX(-24px); }
.cl-slide-right-enter-from { opacity: 0; transform: translateX(-24px); }
.cl-slide-right-leave-to   { opacity: 0; transform: translateX(24px); }

@media (prefers-reduced-motion: reduce) {
  .cl-backdrop-enter-active,
  .cl-backdrop-leave-active,
  .cl-fade-enter-active,
  .cl-fade-leave-active,
  .cl-slide-left-enter-active,
  .cl-slide-left-leave-active,
  .cl-slide-right-enter-active,
  .cl-slide-right-leave-active,
  .cl-close,
  .cl-nav,
  .cl-thumb,
  .cl-image {
    transition-duration: 80ms;
  }
  .cl-image { filter: none; }
}

/* ─── Mobile ───────────────────────────────────────────── */
@media (max-width: 640px) {
  .cl-backdrop { padding: 12px; }
  .cl-counter  { top: 18px; left: 18px; font-size: 11px; }
  .cl-close    { top: 14px; right: 14px; width: 36px; height: 36px; }
  .cl-close svg { width: 14px; height: 14px; }

  /* Mobile stage: drop the grid columns so the image is full-width
     and the nav buttons overlay the image's left/right edges. The
     desktop grid layout (auto 1fr auto) doesn't fit because the
     image + two 44px buttons exceed the viewport width.
     Flex column with center alignment so the absolutely-positioned
     nav buttons (top: 50%) land on the image's vertical middle even
     when the stage has unused space above and below the photo. */
  .cl-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 8px 0;
  }
  .cl-frame {
    padding: 8px;
    padding-bottom: 10px;
    max-width: 100%;
    max-height: 60vh;
    margin: 0 auto;
  }
  .cl-image {
    max-width: calc(100vw - 40px);  /* viewport minus backdrop+frame padding */
    max-height: calc(60vh - 18px);
  }

  .cl-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 8px 20px -6px rgba(0, 0, 0, 0.55);
  }
  .cl-nav svg { width: 18px; height: 18px; }
  .cl-nav--prev { left: 10px; }
  .cl-nav--next { right: 10px; }
  .cl-nav:hover {
    transform: translateY(-50%) scale(1.04);
    background: rgba(255, 255, 255, 0.94);
  }
  .cl-nav:active { transform: translateY(-50%) scale(0.96); }

  .cl-caption { font-size: 13px; max-width: calc(100vw - 24px); }

  /* Compact thumb rail — 6 thumbs fit in the viewport without
     scrolling. The rail itself still scrolls if there are more. */
  .cl-rail   { gap: 6px; max-width: 100vw; padding: 6px 4px 10px; }
  .cl-thumb  { width: 52px; height: 40px; }
}

/* Extra-small screens (e.g., iPhone SE width 375px) — squeeze a bit more. */
@media (max-width: 380px) {
  .cl-rail  { gap: 5px; }
  .cl-thumb { width: 46px; height: 36px; }
}
</style>
